import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import jobService from '../../services/jobService';

const DeleteJobDialog = ({ isOpen, onClose, job, onJobDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  const isConfirmationValid = confirmationText === job?.title;

  // Reset confirmation text when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationText('');
      setError(null);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    if (!job) return;

    setLoading(true);
    setError(null);
    
    try {
      await jobService.deleteJob(job.id);
      
      console.log('Job deleted successfully:', job.id);
      
      if (onJobDeleted) {
        onJobDeleted(job.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error deleting job:', error);
      setError(error.message || 'Failed to delete job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
              <Icon name="AlertTriangle" size={20} className="text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Delete Job</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this job posting? This action cannot be undone.
            </p>
            
            {/* Job Information */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
              <h3 className="font-medium text-gray-900 mb-2">{job.title}</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center">
                  <Icon name="Building" size={14} className="mr-2" />
                  <span>{job.department}</span>
                </div>
                <div className="flex items-center">
                  <Icon name="MapPin" size={14} className="mr-2" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Clock" size={14} className="mr-2" />
                  <span className="capitalize">{job.employment_type}</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Calendar" size={14} className="mr-2" />
                  <span>Created {new Date(job.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Warning Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-red-800 font-medium mb-1">This will permanently delete:</p>
                  <ul className="text-red-700 space-y-1 ml-2">
                    <li>• The job posting and all its details</li>
                    <li>• Associated candidate applications (if any)</li>
                    <li>• Job timeline and history</li>
                    <li>• All related analytics data</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Status Information */}
            {job.status === 'active' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <Icon name="AlertTriangle" size={16} className="text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-yellow-800 font-medium mb-1">Active Job Warning</p>
                    <p className="text-yellow-700">
                      This job is currently active and may have pending applications. 
                      Consider closing the job first before deletion.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <Icon name="XCircle" size={16} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">
              To confirm deletion, please type the job title below:
            </p>
            <p className="text-sm text-gray-500 mb-3 font-mono bg-gray-100 px-2 py-1 rounded">
              {job.title}
            </p>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type the job title here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              disabled={loading}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            onClick={handleDelete}
            disabled={loading || !isConfirmationValid}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-500 min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Icon name="Trash2" size={16} className="mr-2" />
                Delete Job
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteJobDialog;